import React from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../../layout/SiteHeader'
import SiteFooter from '../../layout/SiteFooter';
import {Helmet} from "react-helmet";

const PrivacyPolicy = () => {

  return (
    <>
      <Helmet>
          <title>Privacy Policy | Oreca Pro</title>
          <link rel="canonical" href={window.location.href} />
      </Helmet>
      <SiteHeader />
      <section className='guest-section relative min-h-[100vh] pb-20'>
        <div className='w-full max-w-[1000px] relative mx-auto text-left px-4 pt-[120px] md:pt-[160px]'>
          <h1 className='text-3xl font-semibold my-5 text-center'>Privacy Policy</h1>
          <p className='text-sm text-gray-400 mb-7 text-center'>Last Updated: November 2025</p>
          
          <div className='space-y-6 text-gray-300'>
            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>1. Introduction</h2>
              <p>
                Oreca Pro ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, 
                use, store, and protect your personal information in compliance with the General Data Protection Regulation (GDPR) 
                and the Promotion of Access to Information Act (PAIA) of South Africa.
              </p>
              <p className='mt-3'>
                By using Oreca Pro, you consent to the data practices described in this policy.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>2. Information We Collect</h2>
              
              <h3 className='text-lg font-semibold text-white mt-4 mb-2'>2.1 Information You Provide</h3>
              <p>When you create an account, we collect:</p>
              <ul className='list-disc list-inside ml-4 mt-2 space-y-1'>
                <li><strong>First Name</strong> - to personalize your experience</li>
                <li><strong>Last Name</strong> - to personalize your experience</li>
                <li><strong>Email Address</strong> - for account verification, login, and communication</li>
                <li><strong>Password</strong> - stored securely using encryption (we never store plain text passwords)</li>
              </ul>

              <h3 className='text-lg font-semibold text-white mt-4 mb-2'>2.2 Information You Create</h3>
              <ul className='list-disc list-inside ml-4 mt-2 space-y-1'>
                <li><strong>Saved Spots</strong> - location coordinates (latitude/longitude) of your custom ocean spots</li>
                <li><strong>Ideal Conditions</strong> - your preferred weather conditions for each spot</li>
                <li><strong>Spot Names</strong> - custom names you assign to your saved locations</li>
              </ul>

              <h3 className='text-lg font-semibold text-white mt-4 mb-2'>2.3 Automatically Collected Information</h3>
              <ul className='list-disc list-inside ml-4 mt-2 space-y-1'>
                <li><strong>Usage Analytics</strong> - through Google Analytics and Google Tag Manager (with your consent)</li>
                <li><strong>Device Information</strong> - browser type, operating system, IP address</li>
                <li><strong>App Interaction Data</strong> - pages viewed, features used, time spent in the App</li>
              </ul>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>3. How We Use Your Information</h2>
              <p>We use your personal information to:</p>
              <ul className='list-disc list-inside ml-4 mt-2 space-y-1'>
                <li>Provide and maintain the Oreca Pro service</li>
                <li>Authenticate your account and maintain security</li>
                <li>Store your saved spots and preferences</li>
                <li>Send you forecast updates and App notifications</li>
                <li>Improve our services based on usage patterns</li>
                <li>Communicate important updates about the App</li>
                <li>Respond to your support requests</li>
              </ul>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>4. Legal Basis for Processing (GDPR)</h2>
              <p>We process your personal data based on:</p>
              <ul className='list-disc list-inside ml-4 mt-2 space-y-1'>
                <li><strong>Contract Performance</strong> - to provide the service you signed up for</li>
                <li><strong>Consent</strong> - for analytics cookies and marketing communications (you can withdraw consent anytime)</li>
                <li><strong>Legitimate Interest</strong> - to improve our service and prevent fraud</li>
              </ul>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>5. Data Storage and Security</h2>
              <p>
                Your data is stored securely using industry-standard practices:
              </p>
              <ul className='list-disc list-inside ml-4 mt-2 space-y-1'>
                <li>Passwords are encrypted using bcrypt hashing</li>
                <li>Data is transmitted over secure HTTPS connections</li>
                <li>Database access is restricted and monitored</li>
                <li>Regular security updates and patches are applied</li>
              </ul>
              <p className='mt-3'>
                We use browser local storage to maintain your login session, which is essential for the App to function. 
                See our <Link to="/cookie-policy" className='text-custom-light-blue underline'>Cookie Policy</Link> for more details.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>6. Third-Party Services</h2>
              
              <h3 className='text-lg font-semibold text-white mt-4 mb-2'>6.1 Open Meteo Weather API</h3>
              <p>
                We retrieve weather forecast data from Open Meteo. Location coordinates of your saved spots are sent to their API 
                to fetch forecasts. Open Meteo does not store or track this information.
              </p>

              <h3 className='text-lg font-semibold text-white mt-4 mb-2'>6.2 Mapbox GL</h3>
              <p>
                We use Mapbox for interactive map functionality. When you use the map, Mapbox may collect technical information 
                as described in their privacy policy.
              </p>

              <h3 className='text-lg font-semibold text-white mt-4 mb-2'>6.3 Google Analytics & Tag Manager</h3>
              <p>
                With your consent, we use Google Analytics to understand how users interact with the App. This helps us improve 
                the user experience. You can opt out of analytics tracking through your browser settings or our cookie preferences.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>7. Data Sharing</h2>
              <p className='font-semibold'>
                We do not sell, rent, or share your personal information with third parties for marketing purposes.
              </p>
              <p className='mt-2'>
                Your data is only shared with the third-party services mentioned above (Open Meteo, Mapbox, Google Analytics) 
                to provide the App's core functionality and improve the service. All data is used internally only.
              </p>
              <p className='mt-3 font-semibold text-white'>
                Privacy Note: All spots you save are completely private and visible only to you. They are not shared with or visible to other Oreca Pro users.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>8. Your Rights (GDPR & PAIA)</h2>
              <p>You have the right to:</p>
              <ul className='list-disc list-inside ml-4 mt-2 space-y-1'>
                <li><strong>Access</strong> - request a copy of your personal data</li>
                <li><strong>Rectification</strong> - correct inaccurate or incomplete data</li>
                <li><strong>Erasure</strong> - delete your account and all associated data</li>
                <li><strong>Data Portability</strong> - receive your data in a machine-readable format</li>
                <li><strong>Withdraw Consent</strong> - opt out of analytics and marketing communications</li>
                <li><strong>Object to Processing</strong> - object to how we use your data</li>
                <li><strong>Lodge a Complaint</strong> - with a supervisory authority</li>
              </ul>
              <p className='mt-3'>
                To exercise these rights, contact us at info@oreca.pro or delete your account through the App settings.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>9. Data Retention</h2>
              <p>
                We retain your personal information for as long as your account is active. When you delete your account, 
                all personal data is permanently removed from our systems within 30 days, except where we are legally required 
                to retain certain information.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>10. Children's Privacy</h2>
              <p>
                Oreca Pro is not intended for children under 13. We do not knowingly collect personal information from children 
                under 13. If you believe we have collected such information, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>11. International Data Transfers</h2>
              <p>
                Your data may be processed in servers located outside of South Africa or the European Union. We ensure 
                appropriate safeguards are in place to protect your information in accordance with GDPR requirements.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of significant changes via email 
                or in-app notification. Your continued use of the App after such changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className='text-xl font-semibold text-white mb-3'>13. Contact Us</h2>
              <p>
                For questions about this Privacy Policy, to exercise your rights, or to submit a PAIA request, contact us at:
              </p>
              <p className='mt-2 ml-4'>
                <strong>Email:</strong> info@oreca.pro<br />
                <strong>Subject:</strong> Privacy Inquiry / PAIA Request
              </p>
            </section>
          </div>
        </div>
      </section>
      <SiteFooter />
    </>
  );
};

export default PrivacyPolicy;
